import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { customerPageSize, useListCustomerInfinite } from '@/api/customer/customer';
import { AlertTypes, Button, Drawer, Icon, SearchBar, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetSelectedCustomer, useUpdateSelectedCustomer } from '@/generated/customer';
import { useGetTranslations } from '@/generated/translations';
import { useDebounce, useIntersectionObserver } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import { CustomerRadio } from './CustomerRadio';
import styles from './CustomerSelector.module.scss';

export const CustomerSelector: React.FC = () => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const drawerRef = React.useRef<HTMLDivElement>(null);
	const { isOpen, handleOpen, handleClose } = useDrawer(drawerRef);

	const ariaControlId = 'customer-selector ';

	const { isIntersecting, ref } = useIntersectionObserver({
		threshold: 0.5,
	});

	const queryClient = useQueryClient();
	const addNotification = useNotificationStore((state) => state.addNotification);
	const { data: selectedCustomer } = useGetSelectedCustomer(segmentationId);

	const [localSelectedCustomer, setLocalSelectedCustomer] = useState(selectedCustomer?.data.id ?? 0);
	const [phrase, setPhrase] = useState('');
	const debouncedPhrase = useDebounce(phrase, 500);

	const {
		data: customers,
		fetchNextPage,
		isFetching,
		hasNextPage,
	} = useListCustomerInfinite(segmentationId, [selectedCustomer?.data.id ?? 0], debouncedPhrase, isOpen);

	React.useEffect(() => {
		if (!hasNextPage || isFetching) return;
		if (isIntersecting) fetchNextPage().then();
	}, [fetchNextPage, isIntersecting, isFetching, hasNextPage]);

	const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
		const { value } = event.target;
		setPhrase(value);
	}, []);

	const handleCustomerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalSelectedCustomer(Number(event.target.value));
	};

	const updateSelectedCustomer = useUpdateSelectedCustomer({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries().then();
				handleClose();
			},
			onError: (axiosError) => {
				const error = axiosError.response?.data;
				if (!error) {
					console.error('Unknown error', axiosError);
					addNotification({
						children: translations?.data.shared.genericErrorMsg,
						type: AlertTypes.DANGER,
					});
				} else {
					const response = axiosError.response;
					if (response && response?.status === 409) {
						// backend changes customer on conflict
						queryClient.invalidateQueries().then();

						addNotification({
							children: translations?.data.customerSelector.customerNotValid,
							type: AlertTypes.WARNING,
						});
					} else {
						addNotification({
							children: translations?.data.shared.genericErrorMsg,
							type: AlertTypes.DANGER,
						});
					}
				}
			},
		},
	});

	const handleSubmit = () => {
		const body = { segmentationId, data: { accountId: Number(localSelectedCustomer) } };

		updateSelectedCustomer.mutate(body);
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				variant={'ghost'}
				size={'sm'}
				onClick={handleOpen}
				aria-expanded={isOpen}
				aria-controls={ariaControlId}
				noRadius
			>
				<span className={classNames(styles.triggerLabelText, 'u-ellipsis')}>{selectedCustomer?.data.name}</span>
				<Icon name={'pencil'} size={'md'} />
			</Button>

			{/* Customer Selector Drawer */}
			<Drawer drawerRef={drawerRef} isOpen={isOpen} id={ariaControlId} desktopWidth={'400px'} direction={'Right'}>
				<Drawer.Header title={translations?.data.customerSelector.chooseCustomer} handleClose={handleClose} />
				<Drawer.Body className={styles.body}>
					<form>
						<SearchBar
							inputId="customerSelectorSearch"
							value={phrase}
							placeholder={translations?.data.customerSelector.searchPlaceholder}
							onChange={handleSearchChange}
						/>
					</form>
					<ul className={styles.resultList}>
						<li className={styles.resultItem}>
							<CustomerRadio
								customerId={selectedCustomer?.data.id ?? 0}
								customerName={selectedCustomer?.data.name}
								customerNumber={selectedCustomer?.data.customerNumber}
								owner={selectedCustomer?.data.owner ?? undefined}
								checked={localSelectedCustomer === selectedCustomer?.data.id}
								onChange={handleCustomerChange}
							/>
						</li>
						{customers?.pages.map((page, i) => (
							<React.Fragment key={i}>
								{page.data.customers.map((item) => {
									return (
										<li className={styles.resultItem} key={item.id}>
											<CustomerRadio
												customerId={item.id}
												customerName={item.name}
												customerNumber={item.customerNumber?.toString()}
												owner={item.owner ?? undefined}
												checked={localSelectedCustomer === item.id}
												onChange={handleCustomerChange}
											/>
										</li>
									);
								})}
							</React.Fragment>
						))}
						{isFetching
							? Array.from(Array(customerPageSize)).map((_, index) => (
									<li className={styles.resultItem} key={index}>
										<CustomerRadio
											customerId={index}
											checked={false}
											onChange={() => {}}
											isSkeleton
										/>
									</li>
								))
							: null}
						<li ref={ref} /> {/* Used by intersection observer */}
					</ul>
				</Drawer.Body>

				<Drawer.Footer className={styles.footer} hasNoPadding>
					<Button variant={'ghost'} isFullWidth onClick={handleClose}>
						{translations?.data.shared.cancel}
					</Button>
					<Button variant={'primary'} isFullWidth onClick={handleSubmit}>
						{translations?.data.customerSelector.chooseCustomer}
					</Button>
				</Drawer.Footer>
			</Drawer>
		</>
	);
};
